import { apiFetch } from "./api";

// ==================== Types ====================

/**
 * Wire twin of website-builder-backend `SessionPublicDto`
 * (src/lib/session-registrations.ts), itself the twin of main-backend
 * `SessionPublicDTO`. Served by the worker's public GET /sessions
 * (frozen P2 contract). Keep field-for-field identical.
 */
export type SessionSpeakerRef = {
  id: string;
  name: string | null;
  photo: string | null;
};

export type SessionPublic = {
  programItemId: string;
  title: string | null;
  description: string | null;
  /** ISO instant or null when the program item has no start time. */
  startTime: string | null;
  endTime: string | null;
  location: string | null;
  speakers: SessionSpeakerRef[];
  /** Configured cap; null = uncapped. */
  capacity: number | null;
  /** Live seats remaining (never negative); null = unlimited. */
  spotsLeft: number | null;
  /** Block kind; the server always emits (absent stored kind → SESSION). */
  kind: "SESSION" | "OFFERING" | "BREAK";
  /**
   * OFFERING blocks only, and only when the request opted into OFFERING
   * rows via `?kinds=` (the default filter excludes them): the bound
   * offering CONTAINER (1:n pivot — a block pins ONE container holding n
   * treatments/"Terminarten"). Availability for OFFERING blocks is
   * SLOT-level — `capacity`/`spotsLeft` stay null; slot pickers call
   * {@link fetchOfferingSlots}. Wire twin: SessionPublicDto
   * (website-builder-backend session-registrations.ts) — change lockstep.
   */
  offeringId?: string;
  /** Number of LIVE treatments in the block's container. */
  treatmentCount?: number;
  /** Slot length in minutes — ONLY when `treatmentCount === 1` (a mixed
   *  figure would lie for multi-treatment containers). */
  durationMinutes?: number;
  /**
   * Whether the CALLING guest already holds a registration on this block.
   *
   * Present on EVERY row of a cookie-authenticated read; ABSENT ENTIRELY
   * for an anonymous one — and absent from every response of a worker that
   * predates the seed. SessionGroup therefore probes for the KEY (`"selected"
   * in row`), never its value: key missing = "this worker cannot tell me
   * what I hold", which forces the legacy acquire-only wire shape.
   *
   * Wire twin: `SessionPublicDto.selected` (website-builder-backend
   * session-registrations.ts) — change lockstep.
   */
  selected?: boolean;
};

export type SessionKind = SessionPublic["kind"];

// ==================== API Functions ====================

/**
 * Live session list for the registration programme step. apiFetch already
 * applies the house client idiom: runtimeConfig.apiBase prefix,
 * credentials: "include", cache: "no-store", and GET-only 5xx retry.
 * The worker additionally sends `cache-control: no-store` — spotsLeft is
 * a live figure, never cache it.
 *
 * `kinds` opts into non-SESSION rows (`?kinds=SESSION,OFFERING,BREAK`).
 * Absent → the worker's tolerant default filter (SESSION only) — the
 * designed protection for stale baked bundles that call bare `/sessions`.
 */
export function fetchSessions(kinds?: SessionKind[]): Promise<SessionPublic[]> {
  const qs =
    kinds && kinds.length > 0
      ? `?kinds=${encodeURIComponent(kinds.join(","))}`
      : "";
  return apiFetch<SessionPublic[]>(`/sessions${qs}`);
}

// ==================== OFFERING slot availability (W3) ====================

/**
 * Wire twin of the worker's `GET /sessions/:programItemId/slots` envelope
 * v2 (runtime.ts `sessionSlots`, 1:n pivot). Slots are treatment-scoped
 * (lane narrowing + THAT treatment's duration/capacity happen server-side)
 * and LAZY per treatment: the envelope carries the container's full
 * treatment list, but `slots` only for `selectedTreatmentId`.
 */
export type OfferingSlot = {
  laneId: string;
  laneName: string;
  /** ISO instants. */
  startDt: string;
  endDt: string;
  /** Live spots; exhausted slots are emitted with 0 — never offer those. */
  spotsLeft: number;
};

export type OfferingMyAppointment = {
  appointmentId: string;
  /** First lane of the appointment; null when the stored doc has none. */
  laneId: string | null;
  startDt: string;
  endDt: string;
  status: string;
  /** Which treatment ("Terminart") the appointment is for — the widget
   *  preselects this pill on prefill. null = legacy/cockpit appointment
   *  without a treatment binding (UNREPRESENTABLE by the form: booking
   *  rows require treatmentId, so such blocks stay ungoverned — the
   *  laneId-null rule's twin). */
  treatmentId: string | null;
};

/** One treatment ("Terminart") of the block's container — pill data for
 *  the two-step picker. */
export type OfferingTreatment = {
  treatmentId: string;
  name: string;
  durationMinutes: number;
  imageUrl: string | null;
};

export type OfferingSlotsResponse = {
  /** Normalised server-side (absent → AUTO_BOOKABLE). REQUEST_BASED →
   *  the widget must NOT offer direct booking (info state only; the
   *  worker rejects such books with 400 offeringNotDirectlyBookable). */
  bookingMode: "AUTO_BOOKABLE" | "REQUEST_BASED";
  /** LIVE treatments of the container, server-sorted (sortOrder, _id). */
  treatments: OfferingTreatment[];
  /** The treatment `slots` are scoped to: `?treatmentId=` when passed,
   *  auto = the sole treatment when n==1, else null (slots then []). */
  selectedTreatmentId: string | null;
  slots: OfferingSlot[];
  /** H1 rule: EXACTLY one active appointment in the block's CONTAINER →
   *  that one (prefill/reschedule anchor); none OR ≥2 → null (≥2
   *  additionally sets {@link multipleAppointments}). */
  myAppointment: OfferingMyAppointment | null;
  /** H1: the guest holds ≥2 active appointments in the container (an
   *  admin-managed package) — the widget renders a non-interactive info
   *  state and the block is UNGOVERNED (never swept, never rebooked). */
  multipleAppointments?: true;
};

/**
 * Slot availability for one OFFERING program block. Cookie-authenticated
 * (the registrant has a runtime session by the time DataInput renders);
 * 404 for foreign/non-OFFERING/unbound/deleted-offering blocks and for a
 * `treatmentId` that is not a live treatment of the block's container.
 */
export function fetchOfferingSlots(
  programItemId: string,
  treatmentId?: string,
): Promise<OfferingSlotsResponse> {
  const qs =
    treatmentId !== undefined && treatmentId !== ""
      ? `?treatmentId=${encodeURIComponent(treatmentId)}`
      : "";
  return apiFetch<OfferingSlotsResponse>(
    `/sessions/${programItemId}/slots${qs}`,
  );
}

// ==================== 409 sessionFull parsing ====================

/**
 * Recognise the worker's 409 payload
 * `{ errorCode: "sessionFull", fullProgramItemIds: string[] }` (frozen P2
 * contract, runtime.ts dataInputSubmit). Returns the full ids (possibly
 * empty) when the payload IS a sessionFull error, null for anything else
 * so callers fall through to generic error handling.
 */
export function parseSessionFullError(data: unknown): string[] | null {
  if (data === null || typeof data !== "object") return null;
  const rec = data as Record<string, unknown>;
  if (rec.errorCode !== "sessionFull") return null;
  const ids = rec.fullProgramItemIds;
  return Array.isArray(ids)
    ? ids.filter((x): x is string => typeof x === "string")
    : [];
}

/**
 * PB-N2-2 — recognise the worker's 409
 * `{ errorCode: "optionCapacityFull", fieldId, option }` (runtime.ts
 * dataInputSubmit).
 *
 * The payload has always carried WHICH option filled up; the runtime just
 * dropped it and rendered the bare `message` — so a guest whose pick sold out
 * mid-registration saw the literal string `optionCapacityFull` in the form's
 * alert. Contrast the sold-out EVENT lane, which has always shipped a human
 * sentence.
 *
 * Returns the option label (or "" when the envelope omits it, so the caller
 * can fall back to the generic phrasing) and null for anything else, so
 * unrelated errors keep their existing handling. Mirrors
 * {@link parseSessionFullError}.
 */
export function parseOptionCapacityFullError(
  data: unknown,
): { fieldId: string; option: string } | null {
  if (data === null || typeof data !== "object") return null;
  const rec = data as Record<string, unknown>;
  if (rec.errorCode !== "optionCapacityFull") return null;
  return {
    fieldId: typeof rec.fieldId === "string" ? rec.fieldId : "",
    option: typeof rec.option === "string" ? rec.option : "",
  };
}

/**
 * 409 `resubmitBlocked` — the organizer closed changes.
 *
 * Two reasons produce it (the manual switch, and the change deadline) and the
 * guest does not need to know which; both mean "your answer is on file, the
 * form is shut". Added 2026-08-14: the Worker has answered with this code
 * since the block shipped, and the runtime had no branch for it, so the guest
 * saw a generic failure with the raw code in it after filling in everything.
 *
 * Returns the organizer's OWN message, unlike the quota refusals right below.
 * The difference is deliberate: that envelope leaks internal segment names, so
 * it gets a neutral localised sentence. This one carries text the organizer
 * wrote for their guests, already resolved into the guest's language by
 * `resubmitBlockedBody`, and replacing it with our own wording would throw
 * away the only place they can explain WHY changes are closed. Empty string
 * when the envelope omits it, so the caller can fall back.
 */
export function parseResubmitBlockedError(data: unknown): string | null {
  if (data === null || typeof data !== "object") return null;
  const rec = data as Record<string, unknown>;
  if (rec.errorCode !== "resubmitBlocked") return null;
  return typeof rec.message === "string" ? rec.message : "";
}

/**
 * The two 403 capacity refusals of a public registration: a full segment
 * quota, and the per-guest companion cap.
 *
 * Returns a KIND and nothing else, on purpose. The worker's envelope has
 * carried the organiser's internal segment name (`Quota "VIP-Kontingent" is
 * full`, plus `quotaName` / `groupValue` fields), and the runtime rendered
 * that raw `message` straight to the guest — untranslated, and disclosing
 * cockpit bookkeeping to whoever was refused a seat. Product decision
 * 2026-08-12: one neutral localised sentence per kind, the internal name
 * stays in the cockpit. Handing back no interpolatable text is what keeps
 * that decision from eroding at the next call site.
 */
export function parseCapacityRefusal(
  data: unknown,
): { kind: "quotaFull" | "companionLimitReached" } | null {
  if (data === null || typeof data !== "object") return null;
  const code = (data as Record<string, unknown>).errorCode;
  if (code === "quotaFull") return { kind: "quotaFull" };
  if (code === "companionLimitReached") return { kind: "companionLimitReached" };
  return null;
}

// ==================== OFFERING submit-error parsing (W3) ====================

export type OfferingConflict = {
  programItemId: string;
  /** SlottingConflictDetector code passthrough (e.g. slot_full,
   *  guest_conflict) — diagnostic only; the UX is always refetch + re-pick. */
  code: string;
};

/**
 * Recognise the worker's 409 payload `{ errorCode: "offeringSlotConflict",
 * programItemId, code }` (runtime.ts dataInputSubmit, W2 R1). Mirrors
 * {@link parseSessionFullError}: returns the conflict when the payload IS
 * an offeringSlotConflict error, null for anything else so callers fall
 * through to generic error handling.
 */
export function parseOfferingConflictError(
  data: unknown,
): OfferingConflict | null {
  if (data === null || typeof data !== "object") return null;
  const rec = data as Record<string, unknown>;
  if (rec.errorCode !== "offeringSlotConflict") return null;
  return {
    programItemId:
      typeof rec.programItemId === "string" ? rec.programItemId : "",
    code: typeof rec.code === "string" ? rec.code : "",
  };
}

/**
 * Recognise the worker's 400 payload `{ errorCode:
 * "offeringNotDirectlyBookable", programItemId }` — the backstop for
 * REQUEST_BASED offerings (the widget normally hides the picker via
 * `bookingMode`). Returns the programItemId (possibly "") when the payload
 * IS that error, null otherwise.
 */
export function parseOfferingNotBookableError(data: unknown): string | null {
  if (data === null || typeof data !== "object") return null;
  const rec = data as Record<string, unknown>;
  if (rec.errorCode !== "offeringNotDirectlyBookable") return null;
  return typeof rec.programItemId === "string" ? rec.programItemId : "";
}

/**
 * Recognise the worker's 409 payload `{ errorCode:
 * "offeringMultipleAppointments", programItemId }` (H1: the guest holds ≥2
 * active appointments in the block's container and none matches the
 * desired treatment — an admin-managed package the form cannot express).
 * Returns the programItemId (possibly "") when the payload IS that error,
 * null otherwise. The relayed refetch flips the card into its
 * "multiple appointments" info state (the fresh envelope carries
 * `multipleAppointments: true`).
 */
export function parseOfferingMultipleAppointmentsError(
  data: unknown,
): string | null {
  if (data === null || typeof data !== "object") return null;
  const rec = data as Record<string, unknown>;
  if (rec.errorCode !== "offeringMultipleAppointments") return null;
  return typeof rec.programItemId === "string" ? rec.programItemId : "";
}
